import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import AdminLayout from '@/components/AdminLayout';
import { Card } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';
import { format, startOfMonth, endOfMonth, subMonths, addMonths } from 'date-fns';
import { es } from 'date-fns/locale';
import { 
  DollarSign, Banknote, CreditCard, ChevronLeft, ChevronRight,
  ArrowLeftRight, Package, Store, Clock, Calendar
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDailyCutProducts() {
  const { user, getAuthHeaders } = useAuth();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);

  const isMasterAdmin = user?.role === 'master_admin';

  useEffect(() => {
    fetchBranches();
  }, []);

  useEffect(() => {
    fetchMonthlySales();
  }, [currentMonth, selectedBranch]);

  const fetchBranches = async () => {
    try {
      const response = await axios.get(`${API}/branches`, getAuthHeaders());
      let availableBranches = response.data;
      
      // Manager and Admin can only see their assigned branch
      if ((user?.role === 'manager' || user?.role === 'admin') && user?.branch_id) {
        availableBranches = response.data.filter(b => b.id === user.branch_id);
        setSelectedBranch(user.branch_id);
      }
      
      setBranches(availableBranches);
    } catch (error) {
      // error handled by toast
    }
  };

  const fetchMonthlySales = async () => {
    setLoading(true);
    try {
      const monthStart = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
      const monthEnd = format(endOfMonth(currentMonth), 'yyyy-MM-dd');
      const branchParam = selectedBranch !== 'all' ? `&branch_id=${selectedBranch}` : '';
      const response = await axios.get(
        `${API}/sales?date_from=${monthStart}T00:00:00&date_to=${monthEnd}T23:59:59${branchParam}`,
        getAuthHeaders()
      );
      setSales(response.data);
    } catch (error) {
      toast.error('Error al cargar ventas del mes');
    } finally {
      setLoading(false);
    }
  };

  const goToPreviousMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const goToNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
  const goToCurrentMonth = () => setCurrentMonth(new Date());

  // Filter only products from sales (excluding cancelled sales and cancelled items)
  const productItems = sales
    .filter(sale => !sale.cancelled) // Exclude cancelled sales
    .flatMap(sale => 
      sale.items
        .filter(item => item.item_type === 'product' && !item.cancelled) // Exclude cancelled items
        .map(item => ({
          ...item,
          saleId: sale.id,
          saleDate: sale.created_at?.split('T')[0] || '',
          saleTime: sale.created_at,
          branchName: sale.branch_name,
          userName: sale.user_name,
          paymentMethod: sale.payment_method
        }))
    );

  // Calculate totals (only from non-cancelled sales and items)
  const activeSales = sales.filter(s => !s.cancelled);
  const totals = {
    total: productItems.reduce((sum, item) => sum + (item.price * item.quantity), 0),
    count: productItems.reduce((sum, item) => sum + item.quantity, 0),
    byPayment: {
      cash: activeSales.filter(s => s.payment_method === 'cash')
        .reduce((sum, s) => sum + s.items.filter(i => i.item_type === 'product' && !i.cancelled).reduce((a, i) => a + i.price * i.quantity, 0), 0),
      card: activeSales.filter(s => s.payment_method === 'card')
        .reduce((sum, s) => sum + s.items.filter(i => i.item_type === 'product' && !i.cancelled).reduce((a, i) => a + i.price * i.quantity, 0), 0),
      transfer: activeSales.filter(s => s.payment_method === 'transfer')
        .reduce((sum, s) => sum + s.items.filter(i => i.item_type === 'product' && !i.cancelled).reduce((a, i) => a + i.price * i.quantity, 0), 0),
    }
  };

  // Group by branch
  const byBranch = branches.map(branch => {
    const branchProducts = productItems.filter(p => {
      const sale = sales.find(sale => sale.id === p.saleId);
      return sale?.branch_id === branch.id;
    });
    return {
      branchId: branch.id,
      branchName: branch.name,
      total: branchProducts.reduce((sum, item) => sum + (item.price * item.quantity), 0),
      count: branchProducts.reduce((sum, item) => sum + item.quantity, 0)
    };
  }).filter(b => b.count > 0);

  // Group products by name for summary
  const productSummary = productItems.reduce((acc, item) => {
    if (!acc[item.name]) {
      acc[item.name] = { name: item.name, quantity: 0, total: 0 };
    }
    acc[item.name].quantity += item.quantity;
    acc[item.name].total += item.price * item.quantity;
    return acc;
  }, {});

  // Group by day for daily breakdown
  const byDay = productItems.reduce((acc, item) => {
    const day = item.saleDate;
    if (!acc[day]) {
      acc[day] = { date: day, items: [], total: 0, count: 0 };
    }
    acc[day].items.push(item);
    acc[day].total += item.price * item.quantity;
    acc[day].count += item.quantity;
    return acc;
  }, {});

  const dailyBreakdown = Object.values(byDay).sort((a, b) => b.date.localeCompare(a.date));

  const getPaymentIcon = (method) => {
    switch (method) {
      case 'cash': return <Banknote className="w-4 h-4 text-green-500" />;
      case 'card': return <CreditCard className="w-4 h-4 text-blue-500" />;
      case 'transfer': return <ArrowLeftRight className="w-4 h-4 text-purple-500" />;
      default: return <DollarSign className="w-4 h-4 text-zinc-500" />;
    }
  };

  const StatCard = ({ title, value, icon: Icon, subtitle }) => (
    <Card className="bg-zinc-900/50 border-zinc-800 p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-zinc-500 uppercase tracking-widest mb-1">{title}</p>
          <p className="font-heading text-2xl text-blue-400">{value}</p>
          {subtitle && <p className="text-sm text-zinc-500 mt-1">{subtitle}</p>}
        </div>
        <div className="w-10 h-10 rounded-sm bg-blue-500/10 flex items-center justify-center">
          <Icon className="w-5 h-5 text-blue-400" />
        </div>
      </div>
    </Card>
  );

  const monthName = format(currentMonth, 'MMMM yyyy', { locale: es });
  const isCurrentMonth = format(currentMonth, 'yyyy-MM') === format(new Date(), 'yyyy-MM');

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-monthly-cut-products">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl text-white tracking-tight flex items-center gap-3">
              <Package className="w-8 h-8 text-blue-400" />
              Corte Mensual - Productos
            </h1>
            <p className="text-zinc-500 mt-1">Resumen de productos vendidos por mes</p>
          </div>
          <div className="flex gap-3">
            <Select 
              value={selectedBranch} 
              onValueChange={setSelectedBranch}
              disabled={!isMasterAdmin && user?.branch_id}
            >
              <SelectTrigger className="w-[180px] bg-zinc-900 border-zinc-800 text-white">
                <SelectValue placeholder="Sucursal" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                {isMasterAdmin && (
                  <SelectItem value="all" className="text-white">Todas las sucursales</SelectItem>
                )}
                {branches.map((branch) => (
                  <SelectItem key={branch.id} value={branch.id} className="text-white">
                    {branch.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Month Selector */}
        <Card className="bg-zinc-900/50 border-zinc-800 p-4">
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="icon"
              onClick={goToPreviousMonth}
              className="bg-zinc-800 border-zinc-700 hover:bg-zinc-700"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div className="text-center">
              <p className="text-2xl font-heading text-white capitalize">{monthName}</p>
              {!isCurrentMonth && (
                <Button
                  variant="link"
                  onClick={goToCurrentMonth}
                  className="text-blue-400 text-sm mt-1"
                >
                  Ir al mes actual
                </Button>
              )}
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={goToNextMonth}
              disabled={isCurrentMonth}
              className="bg-zinc-800 border-zinc-700 hover:bg-zinc-700 disabled:opacity-50"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </Card>

        {loading ? (
          <div className="text-center py-12 text-zinc-500">Cargando...</div>
        ) : (
          <>
            {/* Main Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-blue-500/10 border-blue-500/30 p-5 col-span-2">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs text-blue-400 uppercase tracking-widest mb-1">Total Productos del Mes</p>
                    <p className="font-heading text-4xl text-blue-400">${totals.total.toFixed(2)}</p>
                    <p className="text-sm text-zinc-400 mt-1">{totals.count} unidades vendidas</p>
                  </div>
                  <div className="w-12 h-12 rounded-sm bg-blue-500/20 flex items-center justify-center">
                    <Package className="w-6 h-6 text-blue-400" />
                  </div>
                </div>
              </Card>
              <StatCard 
                title="Efectivo" 
                value={`$${totals.byPayment.cash.toFixed(2)}`}
                icon={Banknote}
              />
              <StatCard 
                title="Tarjeta" 
                value={`$${totals.byPayment.card.toFixed(2)}`}
                icon={CreditCard}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <StatCard 
                title="Transferencia" 
                value={`$${totals.byPayment.transfer.toFixed(2)}`}
                icon={ArrowLeftRight}
              />
              <StatCard 
                title="Días con Ventas" 
                value={dailyBreakdown.length}
                icon={Calendar}
              />
            </div>

            {/* Product Summary */}
            {Object.keys(productSummary).length > 0 && (
              <Card className="bg-zinc-900/50 border-zinc-800 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Package className="w-4 h-4 text-blue-400" />
                  <h3 className="text-sm text-zinc-500 uppercase tracking-widest">
                    Resumen de Productos Vendidos
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-zinc-800">
                        <th className="text-left text-xs text-zinc-500 uppercase tracking-widest pb-3">Producto</th>
                        <th className="text-right text-xs text-zinc-500 uppercase tracking-widest pb-3">Cantidad</th>
                        <th className="text-right text-xs text-zinc-500 uppercase tracking-widest pb-3">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.values(productSummary).sort((a, b) => b.total - a.total).map((product) => (
                        <tr key={product.name} className="border-b border-zinc-800/50 last:border-0">
                          <td className="py-3 text-white">{product.name}</td>
                          <td className="py-3 text-right text-zinc-400">{product.quantity}</td>
                          <td className="py-3 text-right text-blue-400 font-semibold">${product.total.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {/* By Branch */}
            {byBranch.length > 0 && selectedBranch === 'all' && (
              <Card className="bg-zinc-900/50 border-zinc-800 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Store className="w-4 h-4 text-blue-400" />
                  <h3 className="text-sm text-zinc-500 uppercase tracking-widest">
                    Productos por Sucursal
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-zinc-800">
                        <th className="text-left text-xs text-zinc-500 uppercase tracking-widest pb-3">Sucursal</th>
                        <th className="text-right text-xs text-zinc-500 uppercase tracking-widest pb-3">Unidades</th>
                        <th className="text-right text-xs text-zinc-500 uppercase tracking-widest pb-3">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {byBranch.map((branch) => (
                        <tr key={branch.branchId} className="border-b border-zinc-800/50 last:border-0">
                          <td className="py-3 text-white">{branch.branchName}</td>
                          <td className="py-3 text-right text-zinc-400">{branch.count}</td>
                          <td className="py-3 text-right text-blue-400 font-semibold">${branch.total.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {/* Daily Breakdown */}
            {dailyBreakdown.length > 0 ? (
              <Card className="bg-zinc-900/50 border-zinc-800 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Calendar className="w-4 h-4 text-blue-400" />
                  <h3 className="text-sm text-zinc-500 uppercase tracking-widest">
                    Desglose por Día ({dailyBreakdown.length} días)
                  </h3>
                </div>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3 pr-4">
                    {dailyBreakdown.map((day) => (
                      <div 
                        key={day.date}
                        className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-4"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-blue-400" />
                            <span className="text-white font-medium">
                              {format(new Date(day.date + 'T12:00:00'), 'EEEE d', { locale: es })}
                            </span>
                          </div>
                          <div className="text-right">
                            <span className="text-blue-400 font-heading text-lg">${day.total.toFixed(2)}</span>
                            <span className="text-zinc-500 text-sm ml-2">({day.count} uds)</span>
                          </div>
                        </div>
                        <div className="space-y-1">
                          {day.items.map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between text-sm py-1 border-t border-zinc-800/50 first:border-0">
                              <div className="flex items-center gap-2">
                                <Package className="w-3 h-3 text-zinc-500" />
                                <span className="text-zinc-300">{item.name}</span>
                                <span className="text-zinc-600">x{item.quantity}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                {getPaymentIcon(item.paymentMethod)}
                                <span className="text-zinc-400">${(item.price * item.quantity).toFixed(2)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </Card>
            ) : (
              <Card className="bg-zinc-900/50 border-zinc-800 p-12 text-center">
                <Package className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-500">No hay productos vendidos en {monthName}</p>
              </Card>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
